import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "./PaymentSummaryModal.css";
import {
  formatDateLessThanOneDay,
  formatDateWithOrdinal,
} from "../../helper/utils";
import PaymentGatewayModal from "../paymentGatewayModal/PaymentGatewayModal";

const PaymentSummaryModal = ({
  isOpen,
  onClose,
  onBack,
  event,
  selectedOptions,
  deliveryAddress,
  quantities,
  isAttirePurchase,
  applyServiceCharge = false,
  user,
}) => {
  const navigate = useNavigate();
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyingError, setVerifyingError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("idle");
  const [isClaimingFreeTicket, setIsClaimingFreeTicket] = useState(false);

  const preferredCurrency = event?.preferredCurrency || "NGN";

  const serviceChargeRate = event?.serviceCharge || 0.05;

  const isFreeTicket = () => {
    if (!selectedOptions || selectedOptions.length === 0) return false;
    return selectedOptions.every((option) => parseFloat(option.price) === 0);
  };

  const formatCurrency = (amount) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;

    if (num === 0) {
      return "Free";
    }

    const symbol = preferredCurrency === "NGN" ? "₦" : "$";
    return `${symbol}${num.toLocaleString()}`;
  };

  const calculateSubtotal = () => {
    return selectedOptions.reduce((total, option) => {
      const price = parseFloat(option.price);
      const quantity =
        quantities[isAttirePurchase ? option.attireTag : option.type] || 1;
      return total + price * quantity;
    }, 0);
  };

  const calculateServiceCharge = () => {
    if (!applyServiceCharge) {
      return 0;
    }
    return calculateSubtotal() * serviceChargeRate;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateServiceCharge();
  };

  const handlePaystackSuccess = async (res) => {
    if (!res?.reference) return;

    setIsVerifying(true);
    setVerificationStatus("verifying");
    setVerifyingError(null);

    try {
      const response = await axios.get("/paystack/verify", {
        params: {
          reference: res.reference,
          source: "web",
        },
      });

      if (response.data.status === "success") {
        setTimeout(() => {
          window.location.href = `/eventManagementDetails/${event._id}`;
        }, 800);
      } else {
        setVerificationStatus("failed");
        setVerifyingError("Payment verification failed");
        setIsVerifying(false);
      }
    } catch (err) {
      setVerificationStatus("failed");
      setVerifyingError("Unable to verify payment");
      setIsVerifying(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (isFreeTicket()) {
      setIsClaimingFreeTicket(true);
      try {
        const response = await axios.post("/tickets/claim-free", {
          guestId: user?._id,
          eventId: event?._id,
          ticketType: selectedOptions[0]?.type,
          guestName: `${user?.firstName} ${user?.lastName}`,
          guestEmail: user?.email,
        });

        if (response.data.success) {
          alert("Free ticket claimed successfully!");
          navigate(`/my-tickets`);
        }
      } catch (error) {
        console.error("Error claiming free ticket:", error);
        alert("Failed to claim free ticket. Please try again.");
      } finally {
        setIsClaimingFreeTicket(false);
      }
      return;
    }

    const total = calculateTotal();
    const subtotal = calculateSubtotal();
    const serviceCharge = calculateServiceCharge();

    let paymentFor;
    let description;
    let amountToPay;

    const isCashGift =
      selectedOptions?.[0]?.type?.toLowerCase() === "cash gift";

    const isTicket = selectedOptions.some(
      (opt) => opt.purchaseType === "TICKET"
    );

    if (isAttirePurchase) {
      paymentFor = "ATTIRE";

      const attireMap = {};

      selectedOptions.forEach((opt) => {
        const qty = quantities[opt.attireTag] || 1;

        attireMap[opt.attireTag] = {
          price: opt.price,
          quantity: qty,
          uri: opt.uri || "",
        };
      });

      description = attireMap;
      amountToPay = total;
    }

    if (isTicket) {
      paymentFor = "TICKET";

      description = selectedOptions.map((opt) => ({
        ticketType: opt.type,
        ticketPrice: opt.price,
        quantity: quantities[opt.type] || 1,
      }));

      amountToPay = selectedOptions.map((opt) => opt.price);
    }

    if (isCashGift) {
      paymentFor = "CASHGIFT";

      description = selectedOptions[0]?.description;
      amountToPay = selectedOptions[0]?.price;
    }

    const paymentDetails = {
      totalAmount: total,
      subTotalAmount: subtotal,
      serviceCharge,
      amountToPay,

      guestName: `${user?.firstName} ${user?.lastName}`,
      guestEmail: user?.email,
      guestId: user?._id,
      hostId: event?.userId,
      eventId: event?._id,

      paymentFor,
      description: JSON.stringify(description),

      numberOfGuest: selectedOptions.reduce(
        (sum, opt) =>
          sum + (quantities[isAttirePurchase ? opt.attireTag : opt.type] || 1),
        0
      ),

      deliveryAddress: isAttirePurchase ? deliveryAddress : null,
      refundPolicy: event?.eventAttire?.refundPolicy || null,
      currency: preferredCurrency,
    };

    setPaymentData(paymentDetails);
    setShowGatewayModal(true);
  };

  const handlePaymentInitiate = async (gateway, data, onCheckoutStart) => {
    try {
      const amount = parseFloat(data.totalAmount);
      if (isNaN(amount) || amount <= 0) {
        alert("Invalid amount. Please try again.");
        console.error("Invalid amount:", data.totalAmount);
        return;
      }

      let response;

      switch (gateway) {
        case "moniepoint":
          response = await axios.post("/monnify/initialize", {
            ...data,
            amount: amount,
            source: "web",
          });

          if (
            response?.data?.responseMessage &&
            response?.data?.responseBody?.checkoutUrl
          ) {
            onCheckoutStart?.();
            window.location.href = response.data.responseBody.checkoutUrl;
          }
          break;

        case "paystack":
          response = await axios.post("/paystack/initialize", {
            ...data,
            amount: amount,
            source: "web",
          });

          if (!response?.data?.status) {
            // throw new Error("Paystack initialization failed");
            alert("Payment initialization failed. Try again.");
          }
          let reference;
          if (response?.data?.status) {
            reference = response.data.data.reference;
          }

          const handler = window.PaystackPop.setup({
            key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
            email: data.guestEmail,
            amount: amount * 100,
            currency: preferredCurrency,
            ref: reference,

            callback: function (res) {
              handlePaystackSuccess(res);
            },

            onClose: () => {
              if (!isVerifying) {
                // window.location.href = `/eventManagementDetails/${event._id}?tab=others`;
              }
            },
          });

          handler.openIframe();
          onCheckoutStart?.();
          break;

        case "stripe":
          response = await axios.post("/stripe/create-payment-intent", {
            ...data,
            amount: amount,
            currency: "USD",
          });

          if (response?.data?.paymentIntent) {
            onCheckoutStart?.();
            sessionStorage.setItem(
              "paymentIntent",
              JSON.stringify(response.data.paymentIntent)
            );

            navigate("/checkout/stripe", {
              replace: true,
              state: {
                clientSecret: response.data.paymentIntent.client_secret,
                paymentIntent: response.data.paymentIntent,
                fromPayment: true,
              },
            });
          }
          break;

        default:
          alert("Invalid payment gateway selected");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);

      if (error.response?.data?.message) {
        alert(`Payment Error: ${error.response.data.message}`);
      } else if (error.message) {
        alert(`Payment Error: ${error.message}`);
      } else {
        alert("Failed to initiate payment. Please try again.");
      }
    }
  };

  if (!isOpen) return null;

  const serviceChargeAmount = calculateServiceCharge();
  const isFreePurchase = isFreeTicket();

  return (
    <>
      <div className="payment-summary-modal-overlay" onClick={onClose}>
        <div
          className="payment-summary-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={onClose} className="modal-close-button">
            <span>X</span>
          </button>
          <div className="payment-summary-modal-body">
            <h2 className="payment-summary-title">
              {isFreePurchase ? "Claim Free Ticket" : "Payment Summary"}
            </h2>

            <div className="summary-details-wrapper">
              <div className="summary-details-card">
                <img
                  src={event.eventImages?.[0]?.uri || "/images/eventImage.jpg"}
                  alt="Event Image"
                  className="summary-event-image"
                />
                <div className="summary-event-info">
                  <h3 className="summary-event-title">
                    {event.eventTitle || "Untitled Event"}
                  </h3>
                  <p className="summary-event-type">
                    {event.eventType || "Unknown"}
                  </p>
                </div>
              </div>
              <div className="event-details-below">
                <span className="summary-detail-text">
                  <span className="icon">🕒</span>
                  {event.eventTime ||
                    event.startTime ||
                    new Date(
                      event.eventDate || event.startDate || event.nextOccurrence
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                </span>
                <span className="separator"> | </span>
                <span className="summary-detail-text">
                  <span className="summary-icon">📅</span>
                  {formatDateLessThanOneDay(
                    event.eventDate ||
                      event.startDate ||
                      (event.multipleDates && event.multipleDates[0]?.date)
                  )}
                </span>
                <span className="separator"> | </span>
                <span className="summary-detail-text">
                  <span className="summary-icon">📍</span>
                  {event.eventLandmark ||
                    event.eventLocation ||
                    "Not specified"}
                </span>
              </div>
            </div>

            {isAttirePurchase && deliveryAddress && (
              <div className="delivery-address-card">
                <h3 className="delivery-address-title">
                  <span className="delivery-icon">📍</span>Delivery Address
                </h3>
                <p className="delivery-address-text">{deliveryAddress}</p>
                <p className="delivery-caution-text">
                  The attires will be delivered to this address by the host
                </p>
              </div>
            )}

            <div className="payment-breakdown">
              <h3 className="payment-breakdown-title">
                {isFreePurchase ? "Ticket Details" : "Payment Breakdown"}
              </h3>
              {selectedOptions.map((option, index) => (
                <div key={index} className="payment-item-row">
                  <span>
                    {isAttirePurchase ? option.attireTag : option.type} (x
                    {quantities[
                      isAttirePurchase ? option.attireTag : option.type
                    ] || 1}
                    )
                  </span>
                  <span>
                    {formatCurrency(
                      parseFloat(option.price) *
                        (quantities[
                          isAttirePurchase ? option.attireTag : option.type
                        ] || 1)
                    )}
                  </span>
                </div>
              ))}

              {applyServiceCharge && serviceChargeAmount > 0 && (
                <div className="payment-item-row service-charge">
                  <span>Service Charge ({serviceChargeRate * 100}%)</span>
                  <span>{formatCurrency(serviceChargeAmount)}</span>
                </div>
              )}

              <div className="payment-item-row total">
                <span>Total</span>
                <span>{formatCurrency(calculateTotal())}</span>
              </div>
            </div>

            {isFreePurchase && (
              <div className="free-ticket-notice">
                <p>
                  🎉 This is a free ticket! Click the button below to claim your
                  ticket.
                </p>
              </div>
            )}

            <div className="payment-summary-buttons">
              <button onClick={onBack} className="summary-back-button">
                Back
              </button>
              <button
                onClick={handleProceedToPayment}
                className="summary-pay-button"
                disabled={isClaimingFreeTicket}
              >
                {isClaimingFreeTicket
                  ? "Claiming..."
                  : isFreePurchase
                  ? "Claim Free Ticket"
                  : "Proceed to Payment"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isVerifying && (
        <div className="payment-verifying-overlay">
          <div className="payment-verifying-modal">
            {verificationStatus === "verifying" && (
              <>
                <h3>Verifying Payment</h3>
                <p>Please wait, do not close this window.</p>
                <div className="spinner" />
              </>
            )}

            {verificationStatus === "success" && (
              <>
                <h3>Payment Verified</h3>
                <p className="success-text">✅ Payment successful</p>
              </>
            )}

            {verificationStatus === "failed" && (
              <>
                <h3>Verification Failed</h3>
                <p className="error-text">{verifyingError}</p>
              </>
            )}
          </div>
        </div>
      )}

      {showGatewayModal && paymentData && !isFreePurchase && (
        <PaymentGatewayModal
          isOpen={showGatewayModal}
          onClose={() => setShowGatewayModal(false)}
          preferredCurrency={preferredCurrency}
          paymentData={paymentData}
          onPaymentInitiate={handlePaymentInitiate}
        />
      )}
    </>
  );
};

export default PaymentSummaryModal;
